import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  useGetRecheckListDataQuery,
  useUpdateRecheckTableHeadDataMutation,
} from "../../../store/queries/Orders";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/Recheck/recheckSlice";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  addToRecheckList,
  printReport,
  printSlip,
  getExportedData,
  getRecheckExcelDownload,
  getMultipleRecheckStockReceiptPrint,
} from "./api";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import moment from "moment";
import html2pdf from "html2pdf.js";
import { debounce } from "lodash";
import getFilterCount from "../../../utils/getFilterCount";

const useRecheck = () => {
  let paginationOptions = [
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
  ];

  const location = useLocation();
  const [loadingdownload, setLoadingDownload] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get("activeTab");
  const [filterShow, setFilterShow] = useState(false);
  const [selectedItem, setSelectedItem] = useState([]);
  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const recheckState = useSelector((state) => state.recheck);
  const dispatch = useDispatch();
  const [inputData, setInputData] = useState("");

  const StockReceiptRef = useRef(null);
  const recheckOfficeSlipRef = useRef(null);

  const normalizeSelectedIds = (ids) => {
    const normalizedIds = Array.isArray(ids)
      ? ids.filter(Boolean)
      : ids
        ? [ids]
        : [];

    return normalizedIds.length
      ? normalizedIds
      : (recheckState?.selectedId || []).filter(Boolean);
  };

  const validateSingleStockReceiptSelection = (
    ids,
    actionLabel = "this action",
  ) => {
    const selectedIds = normalizeSelectedIds(ids);

    if (!selectedIds.length) {
      toast.error("Please select at least one stock receipt");
      return null;
    }

    if (selectedIds.length > 1) {
      toast.warning(`Please select only one stock receipt for ${actionLabel}`);
      return null;
    }

    return selectedIds;
  };

  const {
    data: recheckList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetRecheckListDataQuery({
    per_page: recheckState?.currentPageSize,
    page: recheckState?.currentPage,
    sort_by: recheckState?.sortBy,
    sort_order: recheckState?.sortOrder,
    start: recheckState?.startDate,
    end: recheckState?.endDate,
    search: recheckState?.search,
    date: recheckState.date ? recheckState.date : [],
    item_type: recheckState.item_type ? recheckState.item_type : [],
    shop: recheckState.shop ? recheckState.shop : [],
    status: recheckState.status ? recheckState.status : [],
    type: activeTab === "certificate" ? 1 : 2,
  });

  const [updateRecheckTableData] = useUpdateRecheckTableHeadDataMutation();

  useEffect(() => {
    dispatch(
      updateConfig((state) => {
        state.activeTab = activeTab;
        state.clearSelection = true;
        state.selectedId = [];
        state.cancelstockState = false;
        state.item_type = [];
        state.shop = [];
        state.status = [];
        state.date = [];
        state.from_date = "";
        state.to_date = "";
      }),
    );

    const timeoutId = setTimeout(() => {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
          state.search = "";
        }),
      );
      setInputData("");
    }, 0); // microtask queue execution after initial render

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const navigation = [
    {
      label: "Certificate",
      title: "Certificate",
      link: `/orders/recheck?activeTab=certificate`,
      active: recheckState?.activeTab === "certificate",
    },
    {
      label: "Stock Receipt",
      title: "Stock Receipt",
      link: `/orders/recheck?activeTab=stock-receipt`,
      active: recheckState?.activeTab === "stock-receipt",
    },
  ];

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        const fields =
          recheckList?.data?.fields && activeTab === "stock-receipt"
            ? {
                ...recheckList.data.fields,
                eway_bill_download: { label: "Eway Bill", sortable: false },
                delivery_challan_download: {
                  label: "Delivery Challan",
                  sortable: false,
                },
                additional_document_download: {
                  label: "Additional Document",
                  sortable: false,
                },
              }
            : recheckList?.data?.fields;
        state.tableFields = fields;
      }),
    );
    // eslint-disable-next-line
  }, [recheckList]);
  useEffect(() => {
    if (recheckState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        }),
      );
    }
    // eslint-disable-next-line
  }, [recheckState.clearSelection]);

  const handleOpenUploadModal = (id) => {
    dispatch(
      updateConfig((state) => {
        state.additionalExcelUploadModal = true;
        state.selectedIdAdditionalUpload = [id];
      }),
    );
  };

  const handleRecheckDownload = (receiptId, key, fileName) => {
    getRecheckExcelDownload(receiptId, key).then((response) => {
      if (response?.data?.data?.url) {
        const { url, extension } = response.data.data;
        let downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.setAttribute(
          "download",
          `${fileName}_${moment(Date.now()).format("MM_DD_YYYY_HH_mm_ss")}.${extension}`,
        );
        downloadLink.target = "_blank";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        toast.success(`${fileName} downloaded successfully.`);
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
    });
  };

  const getRow = (feild, data) => {
    const rows = {
      common: (feild, data) => <p className="pro-mb-0">{data[feild]}</p>,
      gli_number: (feild, data) =>
        recheckState?.activeTab === "stock-receipt" ? (
          <span
            className="pro-mb-0 pro-pnt pro-d-flex pro-items-center pro-gap-2"
            onClick={() => handleOpenUploadModal(data?._id)}
            title="Upload document"
          >
            {data?.gli_number}{" "}
            <span className="material-symbols-outlined">upload</span>
          </span>
        ) : (
          <p className="pro-mb-0">{data?.gli_number}</p>
        ),
      status: (_, data) =>
        data?.status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.status === "Marked" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Marked"}
          </span>
        ) : data?.status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.status}
          </span>
        ),
      eway_bill_download: (_, data) => (
        <p className="pro-mb-0">
          <button
            className="pro-btn pro-btn-primary"
            onClick={() =>
              handleRecheckDownload(data?._id, "eway_bill", "Eway_Bill")
            }
          >
            Download
          </button>
        </p>
      ),
      delivery_challan_download: (_, data) => (
        <p className="pro-mb-0">
          <button
            className="pro-btn pro-btn-primary"
            onClick={() =>
              handleRecheckDownload(
                data?._id,
                "delivery_challan",
                "Delivery_Challan",
              )
            }
          >
            Download
          </button>
        </p>
      ),
      additional_document_download: (_, data) => (
        <p className="pro-mb-0">
          <button
            className="pro-btn pro-btn-primary"
            onClick={() =>
              handleRecheckDownload(
                data?._id,
                "additional_document",
                "Additional_Document",
              )
            }
          >
            Download
          </button>
        </p>
      ),
    };

    return rows?.[feild]?.(feild, data) ?? rows["common"](feild, data);
  };

  const handleSort = (label) => {
    if (recheckState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = recheckState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        }),
      );
    }
  };

  const handleSearchDebounced = debounce((value, dispatch) => {
    dispatch(
      updateConfig((state) => {
        state.search = value;
        state.currentPage = 1;
      }),
    );
  }, 1000);

  const handleSearch = (e) => {
    setInputData(e.target.value);
    handleSearchDebounced(e.target.value, dispatch);
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };
  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
    setInputData("");
  };

  const handleFilterClear = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.item_type = [];
        state.shop = [];
        state.status = [];
        state.date = [];
        state.from_date = "";
        state.to_date = "";
        state.currentPage = 1;
      }),
    );
    setInputData("");
  };
  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      }),
    );
  };
  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
  };

  const handleReissueModal = (e) => {
    // Report click based on tab
    if (recheckState?.activeTab === "certificate") {
      let recheckItems = [];
      dispatch(
        updateConfig((state) => {
          state.showAddReissueModal = !state.showAddReissueModal;
          recheckItems.push(e);
          state.selectedRecheck = recheckItems;
          state.selectedOption = false;
        }),
      );
    } else {
      const selectedIds = validateSingleStockReceiptSelection(e, "Report");
      if (!selectedIds) {
        return;
      }

      // print report function
      let data = {
        id: selectedIds[0],
      };
      printReport(data).then((response) => {
        if (response?.status === 200) {
          let dataType = response?.data?.type;
          let binaryData = [];
          binaryData.push(response?.data);
          let downloadLink = document.createElement("a");
          downloadLink.href = window.URL.createObjectURL(
            new Blob(binaryData, { type: dataType }),
          );
          downloadLink.setAttribute(
            "download",
            `${recheckList?.data?.data?.find((item) => item._id === e?.[0])?.gli_number || ""}_Recheck Report_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.xlsx`,
          );
          document.body.appendChild(downloadLink);
          downloadLink.click();
          toast.success("Report downloaded sucessfully");
        } else {
          toast.error(response.message);
        }
      });
    }
  };

  const handleRecheckModal = (e) => {
    // Get the selected items from the list
    const selectedItems = recheckList?.data?.data.filter((item) =>
      e.includes(item._id),
    );
    setSelectedItem(selectedItems);
    // slip click based on tab
    if (recheckState?.activeTab === "certificate") {
      let recheckItems = [];
      dispatch(
        updateConfig((state) => {
          state.showAddRecheckModal = !state.showAddRecheckModal;
          recheckItems.push(e);
          state.selectedRecheck = recheckItems;
          state.selectedOption = true;
        }),
      );
    } else {
      const selectedIds = normalizeSelectedIds(e);
      if (!selectedIds.length) {
        toast.error("Please select at least one stock receipt");
        return;
      }
      getMultipleRecheckStockReceiptPrint(selectedIds).then((response) => {
        if (response?.data?.status_code === 200) {
          toast.success("Stock Receipt Slip downloaded sucessfully");
          dispatch(
            updateConfig(
              (state) => (state.stockPrintData = response?.data?.data),
            ),
          );
        } else {
          toast.error("something went wrong");
        }
        const printInvoice = () => {
          const element = StockReceiptRef?.current;
          html2pdf(element, {
            margin: [5, 2, 2, 2],
            filename: `${selectedIds.length > 1 ? "" : response?.data?.data?.[0]?.gli_number + "_" || ""}Stock Receipt Slip_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: {
              dpi: 100,
              letterRendering: true,
              useCORS: true,
              logging: true,
              scale: 2,
              scrollY: 0,
            },
            jsPDF: { unit: "mm", format: "a5", orientation: "portrait" },
            pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
            DisablePdfCompression: true,
          })
            .from("element-to-print")
            .outputPdf() // add this to replace implicite .save() method, which triggers file download
            .get("pdf")
            .then(function (pdfObj) {
              pdfObj.autoPrint();
              window.open(pdfObj.output("bloburl"), "F");
            });
        };
        setTimeout(() => {
          printInvoice();
        }, 500);
      });
    }
  };

  const clearSelectedList = () => {
    dispatch(
      updateConfig((state) => {
        state.clearSelection = true;
        state.showAddRecheckModal = !state.showAddRecheckModal;
      }),
    );
  };
  const handleRecheck = () => {
    const params = {
      item_details_id: recheckState.selectedRecheck[0],
    };
    addToRecheckList(params).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success("Certificate added to recheck list successfully");
        clearSelectedList();
        refetch();
      } else {
        toast.error("Something went wrong");
        clearSelectedList();
      }
    });
  };

  const handleClose = () => {
    dispatch(
      updateConfig((state) => {
        state.showAddRecheckModal = false;
        state.showAddReissueModal = false;
      }),
    );
  };
  const handleSelectedId = (id) => {
    // Get the selected items from the list
    const selectedItems = recheckList?.data?.data.filter((item) =>
      id.includes(item._id),
    );

    // Check if any selected item has work_status === "INPROGRESS"
    const hasInProgress = selectedItems.some(
      (item) => item.work_status === "CANCELED",
    );

    // Dispatch Redux update
    dispatch(
      updateConfig((state) => {
        state.selectedId = id;
        state.cancelstockState = hasInProgress; // Update cancelstockState based on condition
      }),
    );
  };

  const handleCancelModal = (e) => {
    const selectedIds = validateSingleStockReceiptSelection(e, "Cancel Stock");
    if (!selectedIds) {
      return;
    }

    dispatch(
      updateConfig((state) => {
        state.showCancelModal = !state.showCancelModal;
      }),
    );
  };

  const handleCancelModalClose = () => {
    dispatch(
      updateConfig((state) => {
        state.showCancelModal = false;
      }),
    );
  };

  const handleOfficeSlipModal = (e) => {
    const selectedIds = normalizeSelectedIds(e);
    if (!selectedIds.length) {
      toast.error("Please select one stock receipt");
      return;
    }
    getMultipleRecheckStockReceiptPrint(selectedIds).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success("Office Slip downloaded sucessfully");
        dispatch(
          updateConfig(
            (state) => (state.stockPrintData = response?.data?.data),
          ),
        );
      } else {
        toast.error("something went wrong");
      }
      const printInvoice = () => {
        const element = recheckOfficeSlipRef?.current;
        html2pdf(element, {
          margin: [2, 2, 2, 2],
          filename: `${selectedIds.length > 1 ? "" : response?.data?.data?.[0]?.gli_number + "_" || ""}Office Slip_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: {
            dpi: 100,
            letterRendering: true,
            useCORS: true,
            logging: true,
            scale: 4,
            scrollY: 0,
          },
          jsPDF: { unit: "mm", format: "a6", orientation: "portrait" },
          pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
          DisablePdfCompression: true,
        });
      };
      setTimeout(() => {
        printInvoice();
      }, 500);
    });
  };
  const handleExcelFileDownload = () => {
    const selectedIds = validateSingleStockReceiptSelection(
      recheckState?.selectedId,
      "Excel Download",
    );
    if (!selectedIds) {
      return;
    }

    setLoadingDownload(true);
    let downloadId = {
      receipt_id: selectedIds[0],
    };
    getExportedData(downloadId).then((response) => {
      setLoadingDownload(false);
      let dataType = response?.data?.type;
      let binaryData = [];
      binaryData.push(response?.data);
      let downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(
        new Blob(binaryData, { type: dataType }),
      );
      downloadLink.setAttribute(
        "download",
        `${recheckList?.data?.data?.find((item) => item._id === recheckState?.selectedId[0])?.gli_number || ""}_Reissue Excel_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.xlsx`,
      );
      document.body.appendChild(downloadLink);
      downloadLink.click();
    });
  };
  const handleExcelUploadClick = (e) => {
    const selectedIds = validateSingleStockReceiptSelection(e, "Excel Upload");
    if (!selectedIds) {
      return;
    }

    dispatch(
      updateConfig((state) => {
        state.excelUploadModal = true;
      }),
    );
  };
  const handleExcelUploadModalClose = () => {
    dispatch(
      updateConfig((state) => {
        state.excelUploadModal = false;
      }),
    );
    refetch();
  };

  const filterCount = useMemo(() => {
    return getFilterCount({
      from_date: recheckState.from_date,
      to_date: recheckState.to_date,
      item_type: recheckState.item_type,
      shop: recheckState.shop,
      status: recheckState.status,
    });
  }, [
    recheckState.from_date,
    recheckState.to_date,
    recheckState.item_type,
    recheckState.shop,
    recheckState.status,
  ]);
  const handleAdditionalExcelUploadModalClose = () => {
    dispatch(
      updateConfig((state) => {
        state.additionalExcelUploadModal = false;
      }),
    );
    refetch();
  };

  return {
    inputData,
    isLoading,
    filterShow,
    filterCount,
    isFetching,
    tableFields,
    recheckList,
    recheckState,
    showEditModal,
    StockReceiptRef,
    recheckOfficeSlipRef,
    tabNavs: navigation,
    paginationOptions,
    updateConfig,
    loadingdownload,
    dispatch,
    getRow,
    refetch,
    handleSort,
    handleClose,
    handleSearch,
    setFilterShow,
    handleRecheck,
    handlePageSize,
    handleClearClick,
    handlePagination,
    handleRecheckModal,
    handleReissueModal,
    handleEditColumnsClick,
    updateRecheckTableData,
    handleOfficeSlipModal,
    handleSelectedId,
    handleCancelModal,
    handleCancelModalClose,
    handleExcelFileDownload,
    handleExcelUploadClick,
    handleExcelUploadModalClose,
    handleFilterClear,
    selectedItem,
    handleOpenUploadModal,
    handleAdditionalExcelUploadModalClose,
  };
};

export default useRecheck;
